import React, { useRef, useEffect, useState } from 'react'
import { gsap } from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { ScrollTranstionWrapperProps } from './ScrollTranstionWrapper.types'

const ScrollTranstionWrapper = ({
  className,
  children,
  rightContent,
  onStartAnimation,
  onRevertAnimation,
  startAnimationOn = 'top top',
  rightContainerWidthPercentage = 40,
  leftContainerTransitionDuration = 0.6,
  rightContainerTransitionDuration = 0.6,
  revertOnEnterBack = false,
  ease = 'expo.out',
  pinDurationInPixels = 0,
  revert
}: ScrollTranstionWrapperProps) => {
  const mainContainer = useRef(null)
  const leftContainer = useRef(null)
  const rightContainer = useRef(null)
  const transitionLeftContainer = useRef<gsap.core.Tween>()
  const transitionRightContainer = useRef<gsap.core.Tween>()
  const [isOpen, setIsOpen] = useState(false)

  const onAnimate = () => {
    if (transitionLeftContainer.current && transitionRightContainer.current) {
      transitionLeftContainer.current.play()
      transitionRightContainer.current.play()
    }

    onStartAnimation && onStartAnimation()
    setIsOpen(true)
  }

  const onRevert = () => {
    onRevertAnimation && onRevertAnimation()
    if (transitionLeftContainer.current && transitionRightContainer.current) {
      transitionLeftContainer.current.reverse()
      transitionRightContainer.current.reverse()
    }
  }

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger)

    transitionLeftContainer.current = gsap.to(leftContainer.current, {
      duration: leftContainerTransitionDuration,
      width: `${100 - rightContainerWidthPercentage}%`,
      ease: ease,
      paused: true
    })
    transitionRightContainer.current = gsap.to(rightContainer.current, {
      duration: rightContainerTransitionDuration,
      x: '0%',
      ease: ease,
      paused: true
    })

    gsap.to(mainContainer.current, {
      scrollTrigger: {
        trigger: mainContainer.current,
        start: startAnimationOn,
        onEnter: () => onAnimate(),
        onEnterBack: revertOnEnterBack ? () => onRevert() : undefined,
        end: () => '+=' + pinDurationInPixels,
        pin: !!pinDurationInPixels
      }
    })

    return () => {
      ScrollTrigger.killAll()
    }
  }, [])

  useEffect(() => {
    if (isOpen && revert) {
      onRevert()
      setIsOpen(false)
    }
  }, [revert])

  return (
    <div className={className}>
      <div
        ref={mainContainer}
        className={`relative flex h-full w-full overflow-hidden`}
      >
        <div ref={leftContainer} className={'h-full w-full'}>
          {children}
        </div>
        <div
          ref={rightContainer}
          className={'absolute inset-y-0 right-0 h-full translate-x-full'}
          style={{ width: `${rightContainerWidthPercentage}%` }}
        >
          {rightContent}
        </div>
      </div>
    </div>
  )
}

export default ScrollTranstionWrapper
